import { create } from 'zustand';
import { contentBBox, type BoardDoc, type Camera, type Rect } from '@scribbleplot/shared';
import { CANVAS } from '../tokens';
import { markStep } from './onboarding';

/**
 * Viewport camera.
 *
 * Deliberately outside the board store: where someone is looking is personal,
 * not shared state. Two collaborators panning independently is the point, and
 * keeping it separate means camera changes never re-render the item tree.
 */

const clampScale = (s: number) => Math.min(CANVAS.maxZoom, Math.max(CANVAS.minZoom, s));

const storageKey = (boardId: string) => `sp.cam.${boardId}`;

function loadCamera(boardId: string): Camera | null {
  try {
    const raw = localStorage.getItem(storageKey(boardId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Camera>;
    if (![parsed.x, parsed.y, parsed.s].every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
    return { x: parsed.x!, y: parsed.y!, s: clampScale(parsed.s!) };
  } catch {
    return null;
  }
}

type CameraStore = {
  cam: Camera;
  /** Element used to translate screen coordinates; set by the canvas. */
  viewport: HTMLElement | null;
  boardId: string | null;

  bind: (boardId: string, viewport: HTMLElement | null) => void;
  set: (cam: Camera) => void;
  panBy: (dx: number, dy: number) => void;
  /** Zoom about a screen point, so the content under the cursor stays put. */
  zoomAt: (factor: number, clientX: number, clientY: number) => void;
  zoomBy: (factor: number) => void;
  reset: () => void;
  fit: (doc: Pick<BoardDoc, 'items' | 'strokes'>) => void;
  centerOn: (rect: Rect, minScale?: number) => void;
  /** Convert a screen point to world coordinates. */
  toWorld: (clientX: number, clientY: number) => [number, number];
  /** Visible world rectangle. */
  viewRect: () => Rect | null;
};

let saveTimer: ReturnType<typeof setTimeout> | null = null;

export const useCamera = create<CameraStore>((set, get) => {
  /** Persist the camera, debounced — panning fires on every pointer frame. */
  const remember = (cam: Camera) => {
    const { boardId } = get();
    if (!boardId) return;
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      try {
        localStorage.setItem(storageKey(boardId), JSON.stringify(cam));
      } catch {
        // Private-browsing quota errors are not worth surfacing.
      }
    }, 400);
  };

  const apply = (cam: Camera) => {
    set({ cam });
    remember(cam);
  };

  return {
    cam: { x: 0, y: 0, s: 1 },
    viewport: null,
    boardId: null,

    bind: (boardId, viewport) => {
      const state = get();
      const changed = state.boardId !== boardId;
      set({
        boardId,
        viewport,
        cam: changed ? (loadCamera(boardId) ?? state.cam) : state.cam,
      });
    },

    set: apply,

    panBy: (dx, dy) => {
      const { cam } = get();
      apply({ ...cam, x: cam.x + dx, y: cam.y + dy });
    },

    zoomAt: (factor, clientX, clientY) => {
      const { cam, viewport } = get();
      if (!viewport) return;
      const rect = viewport.getBoundingClientRect();
      const scale = clampScale(cam.s * factor);
      if (scale === cam.s) return;
      const sx = clientX - rect.left;
      const sy = clientY - rect.top;
      // Keep the world point under the cursor fixed on screen.
      const wx = (sx - cam.x) / cam.s;
      const wy = (sy - cam.y) / cam.s;
      apply({ s: scale, x: sx - wx * scale, y: sy - wy * scale });
      markStep('zoom');
    },

    zoomBy: (factor) => {
      const { viewport } = get();
      if (!viewport) return;
      const rect = viewport.getBoundingClientRect();
      get().zoomAt(factor, rect.left + rect.width / 2, rect.top + rect.height / 2);
    },

    reset: () => {
      const { cam, viewport } = get();
      if (!viewport) return;
      const rect = viewport.getBoundingClientRect();
      get().zoomAt(1 / cam.s, rect.left + rect.width / 2, rect.top + rect.height / 2);
    },

    fit: (doc) => {
      const { viewport } = get();
      if (!viewport) return;
      const rect = viewport.getBoundingClientRect();
      const box = contentBBox(doc);
      if (!box || box.w <= 0 || box.h <= 0) {
        apply({ x: rect.width * 0.35, y: rect.height * 0.3, s: 1 });
        return;
      }
      const pad = rect.width < 768 ? 28 : CANVAS.fitPadding;
      // Never zoom past 1:1 when fitting — magnified content looks broken.
      const scale = clampScale(Math.min((rect.width - pad * 2) / box.w, (rect.height - pad * 2) / box.h, 1));
      apply({
        s: scale,
        x: (rect.width - box.w * scale) / 2 - box.x * scale,
        y: (rect.height - box.h * scale) / 2 - box.y * scale,
      });
    },

    centerOn: (target, minScale = 0.45) => {
      const { cam, viewport } = get();
      if (!viewport) return;
      const rect = viewport.getBoundingClientRect();
      const scale = clampScale(Math.max(cam.s, minScale));
      apply({
        s: scale,
        x: rect.width / 2 - (target.x + target.w / 2) * scale,
        y: rect.height / 2 - (target.y + target.h / 2) * scale,
      });
    },

    toWorld: (clientX, clientY) => {
      const { cam, viewport } = get();
      if (!viewport) return [0, 0];
      const rect = viewport.getBoundingClientRect();
      return [(clientX - rect.left - cam.x) / cam.s, (clientY - rect.top - cam.y) / cam.s];
    },

    viewRect: () => {
      const { cam, viewport } = get();
      if (!viewport) return null;
      const rect = viewport.getBoundingClientRect();
      return { x: -cam.x / cam.s, y: -cam.y / cam.s, w: rect.width / cam.s, h: rect.height / cam.s };
    },
  };
});
